import { SdsesFilterParams } from '@/composables/useSdsFilter';
import { NO_DEPARTMENT_ID, NO_TAG_ID } from '@/lib/constant';
import { SdsFilters } from '@/interfaces/api/general/company';
import { Sds } from '@/interfaces/model/general/sds';
import { StatusId } from '@/lib/enum';
import { defineMessages } from '@/lib/i18n';
import store from '@/store';
import {
  dispatchExportSdses,
  dispatchGetCompanyExportOperations,
  dispatchRiskAssessmentExport,
} from '@/store/main/actionsMain';
import { readAssertedCompanyId, readUserLocale } from '@/store/main/getters';
import { AxiosResponse } from 'axios';
import { ref } from 'vue';
import { i18n } from '@/plugins/vue-i18n';
import {
  EXPORT_FILE_FORMAT_CSV,
  EXPORT_FILE_FORMAT_EXCEL,
} from '@/lib/constant';

const $t = i18n.t.bind(i18n);

const m = defineMessages({
  errorMessageExportCharacterCode: 'エクスポート文字コードを指定してください',
  errorMessageExportFormat: 'エクスポート形式を指定してください',
  errorMessageExportTarget: 'エクスポート対象を指定してください',
  errorMessageExportType: 'エクスポート種別を指定してください',
  errorMessageFileFormat: 'ファイル形式を指定してください',
});

export type ExportSdsesTarget = 'all' | 'selected';
export type ExportSdsesType = 'normal' | 'custom' | 'risk_assessment';
export type ExportSdsesCharacterCode = 'utf-8' | 'shift-jis';

export type ExportOperation = {
  name: string;
  value: string;
  formats?: { name: string; value: string }[];
  selectable_character_code: boolean;
};

export type ExportOperations = {
  normal: ExportOperation[];
  custom?: ExportOperation[];
};

export const useSdsesExport = () => {
  const exportOperations = ref<ExportOperations>({ normal: [] });
  const exportSdsesTarget = ref<ExportSdsesTarget>('all'); // エクスポート対象
  const exportSdsesType = ref<ExportSdsesType>('normal'); // エクスポート種別
  const exportSdsesFileFormat = ref<string>(); //ファイル形式
  const exportSdsesExportFormat = ref<string>(); // エクスポート形式
  const selectableCharacterCode = ref(false); // 文字コードを選べるかどうか
  const exportSdsesCharacterCode = ref<ExportSdsesCharacterCode>('utf-8'); // エクスポート文字コード

  // モーダルの設定情報を取得する
  const fetchCompanyExportOperations = async (
    companyId: string,
    language?: string
  ) => {
    const response: AxiosResponse = await dispatchGetCompanyExportOperations(
      store,
      {
        companyId,
        language,
      }
    );
    if (response?.data) {
      exportOperations.value = response.data;
    }
  };

  const selectableStatusList = StatusId.getDropdownOptions();
  const convertFilterParamsToRequest = (
    filterParams: SdsesFilterParams
  ): SdsFilters => ({
    sds_name: filterParams.sdsName,
    product_name: filterParams.productName,
    manufacturer_name: filterParams.manufacturerName,
    product_code: filterParams.productCode,
    revised_at_start_date: filterParams.revisedAtStartDate,
    revised_at_end_date: filterParams.revisedAtEndDate,
    created_at_start_date: filterParams.createdAtStartDate,
    created_at_end_date: filterParams.createdAtEndDate,
    status_id_list:
      filterParams.statusGroupId.length > 0
        ? selectableStatusList
            .filter((statusId) =>
              filterParams.statusGroupId.includes(statusId.groupId)
            )
            .map((statusId) => statusId.value)
        : undefined,
    user_id_list: filterParams.assigneeUsers,
    department_id_list: filterParams.departmentUuids.filter(
      (id) => id !== NO_DEPARTMENT_ID
    ),
    include_no_department:
      filterParams.departmentUuids.includes(NO_DEPARTMENT_ID),
    tag_id_list: filterParams.tagIds.filter((id) => id !== NO_TAG_ID),
    include_no_tag: filterParams.includeNoTag,
    dynamic_list_id_list: filterParams.dynamicListIds,
    is_latest_only: filterParams.isLatestOnly || false,
    health_hazard_level_list: filterParams.healthHazardLevels,
    physical_hazard_level_list: filterParams.physicalHazardLevels,
    working_env_recommendation_list: filterParams.workingEnvRecommendations,
  });

  const exportSdses = async (
    selectedSdses: Sds[],
    params: SdsFilters,
    isSelectedWholeSdses: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<AxiosResponse<string, any>> => {
    if (!exportSdsesFileFormat.value) {
      // MEMO: 事前にvalidateExportSdsesを必ず呼ぶので実際は到達しない
      throw new Error($t(m.errorMessageFileFormat) as string);
    }
    const selectedSdsesUuid = selectedSdses.map((sds) => sds.uuid);

    // ユーザーの言語設定を取得
    const userLocale = readUserLocale(store);
    const language = userLocale === 'en-US' ? 'en' : 'ja';

    return await dispatchExportSdses(store, {
      companyId: readAssertedCompanyId(store),
      data: {
        ...params,
        export_type: exportSdsesType.value,
        file_format: exportSdsesFileFormat.value,
        export_format: exportSdsesExportFormat.value,
        character_code:
          exportSdsesFileFormat.value !== EXPORT_FILE_FORMAT_EXCEL
            ? exportSdsesCharacterCode.value
            : undefined,
        sds_uuid_list:
          !isSelectedWholeSdses && exportSdsesTarget.value === 'selected'
            ? selectedSdsesUuid
            : undefined,
        language,
      },
    });
  };

  const exportRiskAssessment = async (
    selectedSdses: Sds[],
    params: SdsFilters,
    isSelectedWholeSdses: boolean
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<AxiosResponse<string, any>> => {
    const selectedSdsesUuid = selectedSdses.map((sds) => sds.uuid);
    return await dispatchRiskAssessmentExport(store, {
      companyId: readAssertedCompanyId(store),
      data: {
        ...params,
        sds_uuid_list:
          !isSelectedWholeSdses && exportSdsesTarget.value === 'selected'
            ? selectedSdsesUuid
            : undefined,
      },
    });
  };

  const resetExportSdsesForm = () => {
    exportSdsesTarget.value = 'all';
    exportSdsesType.value = 'normal';
    exportSdsesFileFormat.value = undefined;
    exportSdsesExportFormat.value = undefined;
    selectableCharacterCode.value = false;
    exportSdsesCharacterCode.value = 'utf-8';
  };

  const validateExportSdses = (): string | null => {
    if (!exportSdsesTarget.value) {
      return m.errorMessageExportTarget;
    }
    if (!exportSdsesType.value) {
      return m.errorMessageExportType;
    }

    if (exportSdsesType.value === 'normal') {
      if (!exportSdsesFileFormat.value) {
        return m.errorMessageFileFormat;
      }
      if (
        exportSdsesFileFormat.value === EXPORT_FILE_FORMAT_CSV &&
        !exportSdsesExportFormat.value
      ) {
        return m.errorMessageExportFormat;
      }
      if (
        exportSdsesFileFormat.value !== EXPORT_FILE_FORMAT_EXCEL &&
        !exportSdsesCharacterCode.value
      ) {
        return m.errorMessageExportCharacterCode;
      }
    } else {
      if (!exportSdsesFileFormat.value) {
        return m.errorMessageFileFormat;
      }
      if (!exportSdsesExportFormat.value) {
        return m.errorMessageExportFormat;
      }
      if (!exportSdsesCharacterCode.value) {
        return m.errorMessageExportCharacterCode;
      }
    }

    return null;
  };

  return {
    exportOperations,
    exportSdsesTarget,
    exportSdsesType,
    exportSdsesFileFormat,
    exportSdsesExportFormat,
    selectableCharacterCode,
    exportSdsesCharacterCode,
    fetchCompanyExportOperations,
    resetExportSdsesForm,
    validateExportSdses,
    convertFilterParamsToRequest,
    exportSdses,
    exportRiskAssessment,
  };
};
